import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { plateFont } from '../../utils/fonts';

/**
 * "Kohde": which vehicle or machine a parts-sale line is for. Free text first — a
 * plate, a machine id, "Nosturi 2" — with suggestions that only save typing: the
 * customer's own vehicles, labels used before on this customer's sales and, after
 * two characters, any vehicle of the workshop by plate. Typed text is kept as-is
 * even when no option is picked, and nothing is upper-cased for the seller.
 *
 * Controlled by `value`/`onChange(string)`; persisting is the caller's job
 * (explicit Save buttons, never on blur).
 */
function TargetLabelField({
  value,
  onChange,
  customerId,
  label,
  placeholder,
  helperText,
  size = 'small',
  autoFocus = false,
  disabled = false,
  fullWidth = true,
  sx,
  inputTestId,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);
  const q = (value || '').trim();

  useEffect(() => {
    if (!open) return undefined;
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        if (customerId) params.customer_id = customerId;
        if (q.length >= 2) params.q = q;
        const resp = await axios.get('/api/parts-sales/target-suggestions/', { params });
        if (seq === requestSeq.current) setOptions(Array.isArray(resp.data) ? resp.data : []);
      } catch (e) {
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, q ? 300 : 0);
    return () => clearTimeout(timer);
  }, [open, customerId, q]);

  const groupLabels = {
    vehicle: t('parts_sales.targetGroupVehicles'),
    history: t('parts_sales.targetGroupHistory'),
    org_vehicle: t('parts_sales.targetGroupOrg'),
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      disabled={disabled}
      size={size}
      sx={sx}
      open={open && options.length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      groupBy={(option) => option.kind}
      // The text is the value: no selected-option state to drift from `inputValue`
      // when the caller resets or saves the field.
      value={null}
      inputValue={value || ''}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input' || reason === 'clear') onChange(newValue || '');
      }}
      onChange={(event, newValue) => {
        if (newValue && typeof newValue === 'object') onChange(newValue.label || '');
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 1.5, pt: 1, pb: 0.25, fontWeight: 600, letterSpacing: '0.04em' }}
          >
            {groupLabels[params.group] || ''}
          </Typography>
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={`${option.kind}-${option.label}`} {...rest}>
            <Typography variant="body2" sx={{ fontWeight: 600, ...plateFont }}>
              {option.label}
              {option.hint && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                  {option.hint}
                </Typography>
              )}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          autoFocus={autoFocus}
          autoComplete="off"
          inputProps={{ ...params.inputProps, maxLength: 100, ...(inputTestId ? { 'data-testid': inputTestId } : {}) }}
          InputProps={{
            ...params.InputProps,
            sx: { ...plateFont },
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default TargetLabelField;
